"use client";

// /experiment — Welcome offer banner family (pink).
// 10 distinct compositional directions, not micro-tweaks of one layout.
// Same locked text in all variants. Variant state lives in a shared hook
// so a header-mounted switcher can drive selection without coupling.

import Image from "next/image";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { PicksPIcon } from "@/components/ui/icons";
import { useExperimentVariant } from "@/lib/experiment/use-variant";
import { AddFundsModal } from "@/components/layout/add-funds-modal";

interface Props {
  onClaim?: () => void;
  className?: string;
}

interface PublicProps extends Props {
  /** When set, render this variant (1-based) and ignore the experiment switcher. */
  forceVariant?: number;
}

const PINK = "hsl(335 78% 48%)";
const PINK_DARK = "hsl(335 80% 36%)";
const YELLOW = "#ffd45e";
/* Boost (bonus-eligible) banner — runs blue to match the Boost filter accent.
   BLUE_DARK is the on-blue pill text color. */
const BLUE_DARK = "hsl(222 70% 34%)";
const FONT: React.CSSProperties = {
  fontFamily: "var(--font-open-sauce), system-ui, sans-serif",
};

const STORAGE_KEY = "alexey:experiment-offer-banner";
const VARIANT_LABELS = [
  "Default · chip + yellow accent",
  "Diagonal slab",
  "Number hero — $50.",
  "Vertical 2-tone",
  "Yellow ribbon top",
  "F-mark watermark",
  "Chip cluster (3 stickers)",
  "Postcard frame",
  "Tilted composition",
  "Wordmark headline (Sushi-style)",
  "Yellow ribbon + F-mark watermark",
  "First deposit match · 100% up to $420",
  "Number leads · flat fill",
  "Match deposit · info banner",
] as const;

/* ─── Shared primitives ────────────────────────────────────────────────── */

function Shell({
  children,
  className,
  bg = PINK,
  darkBg = PINK_DARK,
  onClaim,
  ariaLabel = "Claim $50 welcome bonus",
  interactive = true,
  showHalftone = true,
  showDarkAccent = true,
}: {
  children: React.ReactNode;
  className?: string;
  bg?: string;
  darkBg?: string;
  onClaim?: () => void;
  ariaLabel?: string;
  /** When false the banner is a static info surface — no click, no hover. */
  interactive?: boolean;
  /** Paper halftone overlay. Off for surfaces with their own texture (e.g. pitch). */
  showHalftone?: boolean;
  /** Diagonal near-black wash. Off for surfaces that should stay an even color. */
  showDarkAccent?: boolean;
}) {
  const base = "group relative h-[136px] w-full overflow-hidden rounded-[14px] text-left";

  if (!interactive) {
    return (
      <div className={cn(base, className)} style={{ background: bg, ...FONT }}>
        {showDarkAccent && <DarkAccent darkBg={darkBg} />}
        {showHalftone && <Halftone />}
        {children}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClaim}
      aria-label={ariaLabel}
      className={cn(base, "transition-[filter] duration-200 hover:brightness-[1.06] focus:outline-none", className)}
      style={{ background: bg, ...FONT }}
    >
      {showDarkAccent && <DarkAccent darkBg={darkBg} />}
      {showHalftone && <Halftone />}
      {children}
    </button>
  );
}

/* Halftone dot HUD — subtle white dot grid across the whole surface, soft
   overlay blend so it reads as printed paper texture, not loud pattern. */
function Halftone() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage:
          "radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1.4px)",
        backgroundSize: "7px 7px",
        opacity: 0.22,
        mixBlendMode: "overlay",
      }}
    />
  );
}

/* Dark-accent wash — two stacked diagonal gradients ease the base into a
   near-black brand tone toward the lower-right. Top layer is black for
   weight; under-layer keeps a hint of the brand hue. No hard edge. */
function DarkAccent({ darkBg }: { darkBg: string }) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        background: `
          linear-gradient(115deg, transparent 0%, transparent 30%, rgba(0,0,0,0.85) 100%),
          linear-gradient(115deg, transparent 0%, transparent 25%, ${darkBg} 100%)
        `,
        opacity: 0.9,
      }}
    />
  );
}

function Wordmark({
  left = 16,
  top = 16,
  width = 64,
  invert = true,
}: {
  left?: number;
  top?: number;
  width?: number;
  invert?: boolean;
}) {
  return (
    <span className="absolute block" style={{ left, top, width, height: width * 0.22 }}>
      <Image
        src="/picks-logo.svg"
        alt="Picks"
        fill
        sizes={`${width}px`}
        className={cn("object-contain object-left", invert && "brightness-0 invert")}
        priority
      />
    </span>
  );
}

function FiftyChip({
  size = 92,
  fontSize = 32,
  rotate = -8,
  bg = "white",
  fg = PINK,
}: {
  size?: number;
  fontSize?: number;
  rotate?: number;
  bg?: string;
  fg?: string;
}) {
  return (
    <div
      aria-hidden
      className="pointer-events-none flex flex-col items-center justify-center"
      style={{
        transform: `rotate(${rotate}deg)`,
        width: size,
        height: size,
        background: bg,
        borderRadius: size * 0.18,
        boxShadow: `0 6px 0 rgba(0,0,0,0.18), 0 0 0 4px ${bg}`,
      }}
    >
      <span className="font-bold leading-none tracking-[-0.03em]" style={{ color: fg, fontSize }}>$50</span>
      <span className="mt-0.5 text-[8px] font-bold uppercase tracking-[0.18em]" style={{ color: fg }}>Bonus</span>
    </div>
  );
}

function FiveChip({
  size = 48,
  fontSize = 16,
  rotate = 14,
}: { size?: number; fontSize?: number; rotate?: number }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none flex flex-col items-center justify-center"
      style={{
        transform: `rotate(${rotate}deg)`,
        width: size,
        height: size,
        background: YELLOW,
        borderRadius: size * 0.2,
        boxShadow: "0 4px 0 rgba(0,0,0,0.18), 0 0 0 3px " + YELLOW,
      }}
    >
      <span className="font-bold leading-none tracking-[-0.03em] text-black" style={{ fontSize }}>$5</span>
      <span className="mt-0.5 text-[7px] font-bold uppercase tracking-[0.16em] text-black/75">Entry</span>
    </div>
  );
}

function FmarkChip({ size = 92, rotate = -10 }: { size?: number; rotate?: number }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none flex items-center justify-center"
      style={{
        transform: `rotate(${rotate}deg)`,
        width: size,
        height: size,
        background: "white",
        borderRadius: size * 0.18,
        boxShadow: "0 6px 0 rgba(0,0,0,0.18), 0 0 0 4px white",
      }}
    >
      <PicksPIcon className="h-[40px] w-[54px]" style={{ color: PINK }} />
    </div>
  );
}

function CopyBlock({
  pillBg = "white",
  pillFg = PINK,
  align = "left",
  pr = 130,
  pl = 16,
  pt = 26,
  textColor = "white",
  pillVisible = true,
}: {
  pillBg?: string;
  pillFg?: string;
  align?: "left" | "right" | "center";
  pr?: number;
  pl?: number;
  pt?: number;
  textColor?: string;
  pillVisible?: boolean;
}) {
  const eyebrowColor = textColor === "white" ? "text-white/85" : "text-black/65";
  const eyebrowDivider = textColor === "white" ? "bg-white/45" : "bg-black/30";
  const headlineColor = textColor === "white" ? "text-white" : "text-black";
  const sublineColor = textColor === "white" ? "text-white/85" : "text-black/65";
  const alignClass =
    align === "right" ? "items-end text-right" : align === "center" ? "items-center text-center" : "items-start";
  return (
    <div
      className={cn("relative z-10 flex h-full flex-col justify-center gap-1.5", alignClass)}
      style={{ paddingLeft: pl, paddingRight: pr, paddingTop: pt }}
    >
      <div className={cn("flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.24em]", eyebrowColor)}>
        <span>Welcome offer</span>
        <span aria-hidden className={cn("block h-px w-3", eyebrowDivider)} />
        <span>New players only</span>
      </div>
      <h3 className={cn("text-[22px] font-bold leading-[1.05] tracking-tight", headlineColor)}>
        Your Bonus is Ready
      </h3>
      <div className="flex items-center gap-3">
        <span className={cn("text-[12px] font-medium tracking-tight", sublineColor)}>Play now</span>
        {pillVisible && (
          <span
            aria-hidden
            className="inline-flex h-7 items-center rounded-full px-4 text-[12px] font-semibold tracking-tight"
            style={{ background: pillBg, color: pillFg }}
          >
            Claim
          </span>
        )}
      </div>
    </div>
  );
}

/* ─── 10 variants ──────────────────────────────────────────────────────── */

/* V1 — Default: $50 chip right + $5 yellow companion */
function V1({ onClaim, className }: Props) {
  return (
    <Shell onClaim={onClaim} className={className}>
      <Wordmark />
      <div className="absolute" style={{ right: 16, top: "50%", transform: "translateY(-50%)" }}>
        <FiftyChip />
      </div>
      <div className="absolute" style={{ right: 92, top: 14 }}>
        <FiveChip />
      </div>
      <CopyBlock />
    </Shell>
  );
}

/* V2 — Diagonal slab: darker pink triangle from bottom-right */
function V2({ onClaim, className }: Props) {
  return (
    <Shell onClaim={onClaim} className={className}>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: PINK_DARK,
          clipPath: "polygon(60% 0, 100% 0, 100% 100%, 30% 100%)",
        }}
      />
      <Wordmark />
      <div className="absolute" style={{ right: 18, top: "50%", transform: "translateY(-50%)" }}>
        <FiftyChip rotate={-6} />
      </div>
      <CopyBlock />
    </Shell>
  );
}

/* V3 — Number hero: massive "$50." with terminal period, no chip */
function V3({ onClaim, className }: Props) {
  return (
    <Shell onClaim={onClaim} className={className}>
      <Wordmark />
      <div
        className="absolute font-bold leading-none tracking-[-0.04em] text-white"
        style={{ right: 16, top: "50%", transform: "translateY(-50%)", fontSize: 76 }}
      >
        $50<span style={{ color: YELLOW }}>.</span>
      </div>
      <CopyBlock pr={140} />
    </Shell>
  );
}

/* V4 — Vertical 2-tone: pink left, darker pink right */
function V4({ onClaim, className }: Props) {
  return (
    <Shell onClaim={onClaim} className={className}>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0"
        style={{ left: "60%", right: 0, background: PINK_DARK }}
      />
      <Wordmark />
      <div className="absolute" style={{ right: 24, top: "50%", transform: "translateY(-50%)" }}>
        <FiftyChip rotate={0} />
      </div>
      <CopyBlock pr={170} />
    </Shell>
  );
}

/* V5 — Yellow ribbon top + chip on right */
function V5({ onClaim, className }: Props) {
  return (
    <Shell onClaim={onClaim} className={className}>
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 flex h-[22px] items-center px-4 text-[9px] font-medium uppercase tracking-[0.28em] text-black"
        style={{ background: YELLOW }}
      >
        Welcome offer · New players only
      </div>
      <Wordmark top={28} width={52} />
      <div className="absolute" style={{ right: 16, top: 30 }}>
        <FiftyChip size={84} fontSize={28} rotate={-6} />
      </div>
      <div
        className="relative z-10 flex h-full flex-col justify-end gap-1 pl-4 pr-[120px] pb-3 pt-[44px]"
      >
        <h3 className="text-[26px] font-black leading-[0.98] tracking-tight text-white">
          Your Bonus
          <br />
          Is Ready
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-medium tracking-tight text-white/85">Play now</span>
          <span
            aria-hidden
            className="inline-flex h-7 items-center rounded-full bg-white px-4 text-[12px] font-bold tracking-tight"
            style={{ color: PINK }}
          >
            Claim
          </span>
        </div>
      </div>
    </Shell>
  );
}

/* V6 — Giant F-mark watermark behind copy + small chip overlay */
function V6({ onClaim, className }: Props) {
  return (
    <Shell onClaim={onClaim} className={className}>
      <span
        aria-hidden
        className="pointer-events-none absolute"
        style={{ right: -18, top: "50%", transform: "translateY(-50%)", opacity: 0.18 }}
      >
        <PicksPIcon className="h-[160px] w-[210px] text-white" />
      </span>
      <Wordmark />
      <div className="absolute" style={{ right: 18, top: "50%", transform: "translateY(-50%)" }}>
        <FiftyChip size={76} fontSize={26} rotate={-6} />
      </div>
      <CopyBlock pr={110} />
    </Shell>
  );
}

/* V7 — Chip cluster: 3 stickers overlapping playfully on the right */
function V7({ onClaim, className }: Props) {
  return (
    <Shell onClaim={onClaim} className={className}>
      <Wordmark />
      <div className="absolute" style={{ right: 6, top: 6 }}>
        <FiveChip size={42} fontSize={14} rotate={-12} />
      </div>
      <div className="absolute" style={{ right: 54, top: 16 }}>
        <FmarkChip size={48} rotate={14} />
      </div>
      <div className="absolute" style={{ right: 8, top: 50 }}>
        <FiftyChip size={84} fontSize={28} rotate={-8} />
      </div>
      <CopyBlock pr={120} />
    </Shell>
  );
}

/* V8 — Postcard frame: white border ring, pink interior */
function V8({ onClaim, className }: Props) {
  return (
    <Shell onClaim={onClaim} className={className}>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-2 rounded-[10px] border border-dashed border-white/55"
      />
      <Wordmark left={20} top={20} width={56} />
      <div className="absolute" style={{ right: 22, top: "50%", transform: "translateY(-50%)" }}>
        <FiftyChip size={84} fontSize={28} rotate={-6} />
      </div>
      <div className="relative z-10 flex h-full flex-col justify-center gap-1.5 pl-5 pr-[120px] pt-[28px]">
        <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/85">
          Welcome offer · New players only
        </div>
        <h3 className="text-[20px] font-bold leading-[1.05] tracking-tight text-white">
          Your Bonus is Ready
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-[12px] font-medium tracking-tight text-white/85">Play now</span>
          <span
            aria-hidden
            className="inline-flex h-7 items-center rounded-full bg-white px-4 text-[12px] font-semibold tracking-tight"
            style={{ color: PINK }}
          >
            Claim
          </span>
        </div>
      </div>
    </Shell>
  );
}

/* V9 — Tilted composition: wordmark + chip slightly rotated as a unit */
function V9({ onClaim, className }: Props) {
  return (
    <Shell onClaim={onClaim} className={className}>
      <span
        aria-hidden
        className="pointer-events-none absolute"
        style={{ right: -28, top: -22, opacity: 0.12 }}
      >
        <PicksPIcon className="h-[200px] w-[260px] text-white" />
      </span>
      <div style={{ transform: "rotate(-1.5deg)", transformOrigin: "left top" }}>
        <Wordmark />
        <CopyBlock pr={128} />
      </div>
      <div
        className="absolute"
        style={{ right: 14, top: "50%", transform: "translateY(-50%) rotate(4deg)" }}
      >
        <FiftyChip rotate={0} />
      </div>
      <div className="absolute" style={{ right: 92, top: 12 }}>
        <FiveChip rotate={-12} />
      </div>
    </Shell>
  );
}

/* V10 — Sushi-style headline. "Picks." as the title, $50 chip as anchor.
   Brand-as-headline, like the Sushi Tuesday banner direction. */
function V10({ onClaim, className }: Props) {
  return (
    <Shell onClaim={onClaim} className={className}>
      <div
        className="absolute font-bold leading-[0.92] tracking-[-0.025em] text-white"
        style={{ left: 18, top: 16, fontSize: 36 }}
      >
        Picks.
      </div>
      <div className="absolute" style={{ left: 18, top: 60 }}>
        <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/85">
          Welcome offer
        </div>
        <div className="mt-0.5 text-[18px] font-bold tracking-tight text-white">Your Bonus is Ready</div>
        <div className="mt-1 flex items-center gap-2">
          <span className="text-[11px] font-medium tracking-tight text-white/85">Play now</span>
          <span
            aria-hidden
            className="inline-flex h-6 items-center rounded-full bg-white px-3 text-[11px] font-semibold tracking-tight"
            style={{ color: PINK }}
          >
            Claim
          </span>
        </div>
      </div>
      <div className="absolute" style={{ right: 16, top: "50%", transform: "translateY(-50%)" }}>
        <FiftyChip rotate={-8} />
      </div>
      <div className="absolute" style={{ right: 92, top: 14 }}>
        <FiveChip />
      </div>
    </Shell>
  );
}

/* V11 — Canonical reference. Yellow ribbon top, lowercase 2-line headline,
   tilted white sheen accent on the right, F-mark sticker (rotated white
   square with PicksPIcon) replacing the "?" surprise sticker from the
   reference. */
function V11({ onClaim, className }: Props) {
  return (
    <Shell onClaim={onClaim} className={className}>
      {/* Tilted white sheen — soft accent shape behind the sticker */}
      <span
        aria-hidden
        className="pointer-events-none absolute"
        style={{
          right: "-8%",
          top: "-15%",
          width: "60%",
          height: "140%",
          background: "rgba(255,255,255,0.10)",
          transform: "rotate(-10deg)",
          borderRadius: 18,
          zIndex: 1,
        }}
      />

      {/* Yellow ribbon — pixel-feel mono caps */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 z-[5] flex h-[22px] items-center gap-2.5 px-4 font-mono text-[8.5px] font-medium uppercase tracking-[0.20em]"
        style={{ background: YELLOW, color: "#1A1208" }}
      >
        <span>welcome offer</span>
        <span
          aria-hidden
          className="block size-[3px] rounded-full"
          style={{ background: "#1A1208" }}
        />
        <span>new players only</span>
      </div>

      {/* Giant F-mark watermark — low-opacity Picks F bleeds off the right
          edge, anchors the surface (V6 treatment ported into V11). */}
      <span
        aria-hidden
        className="pointer-events-none absolute z-[2]"
        style={{
          right: -14,
          top: "50%",
          transform: "translateY(calc(-50% + 16px))",
          opacity: 0.18,
        }}
      >
        <PicksPIcon className="h-[110px] w-[144px] text-white" />
      </span>


      {/* Content — lowercase 2-line headline, Play now + Claim pill.
          On phone widths the headline + horizontal padding scale fluidly
          so copy never collides with the F-mark watermark. From sm: up
          the original 62% maxWidth + 28px headline + 22px padding apply. */}
      <div
        className="relative z-[4] flex h-full flex-1 flex-col justify-end px-[16px] pb-[14px] pt-[34px] sm:max-w-[62%] sm:px-[22px]"
        style={{ maxWidth: "70%" }}
      >
        <h3
          className="m-0 font-black leading-[0.9] tracking-[-0.025em] text-white lowercase sm:text-[28px]"
          style={{ marginBottom: "auto", fontSize: "clamp(18px, 7.5vw, 28px)" }}
        >
          your bonus
          <br />
          is ready.
        </h3>
        <div className="mt-2 flex items-center gap-2.5">
          <span className="text-[11.5px] font-medium tracking-tight text-white/85">
            Play now
          </span>
          <span
            aria-hidden
            className="inline-flex h-6 items-center rounded-full bg-white px-3 text-[11px] font-extrabold tracking-[-0.005em]"
            style={{
              color: "#C4135C",
              boxShadow:
                "0 4px 10px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.5)",
            }}
          >
            Claim
          </span>
        </div>
      </div>
    </Shell>
  );
}

/* V12 — World Cup matches CTA. Replaces the welcome offer (per Gia: WC pink
   banner instead). Same brand-pink V11 composition; routes to the World Cup
   board filter, same destination as the sidebar CTA. */
function V12({ onClaim, className }: Props) {
  return (
    <Shell onClaim={onClaim} className={className} ariaLabel="View FIFA World Cup picks">
      {/* Tilted white sheen — soft accent shape on the right */}
      <span
        aria-hidden
        className="pointer-events-none absolute"
        style={{
          right: "-8%",
          top: "-15%",
          width: "60%",
          height: "140%",
          background: "rgba(255,255,255,0.10)",
          transform: "rotate(-10deg)",
          borderRadius: 18,
          zIndex: 1,
        }}
      />

      {/* Yellow ribbon — pixel-feel mono caps */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 z-[5] flex h-[22px] items-center gap-2.5 px-4 font-mono text-[8.5px] font-medium uppercase tracking-[0.20em]"
        style={{ background: YELLOW, color: "#1A1208" }}
      >
        <span>fifa world cup 2026</span>
        <span
          aria-hidden
          className="block size-[3px] rounded-full"
          style={{ background: "#1A1208" }}
        />
        <span>lines are live</span>
      </div>

      {/* World Cup trophy watermark — white-ified, bleeding off the right
          edge. Replaces the Picks F-mark for the WC banner. */}
      <span
        aria-hidden
        className="pointer-events-none absolute z-[2]"
        style={{
          right: 22,
          // Sit fully below the 22px yellow ribbon so the trophy top isn't
          // clipped by it; centered in the region beneath the ribbon.
          top: 22,
          bottom: 0,
          transform: "translateY(0)",
          width: 72,
          opacity: 0.22,
        }}
      >
        <Image
          src="/images/leagues/worldcup.svg"
          alt=""
          fill
          sizes="104px"
          className="object-contain brightness-0 invert"
        />
      </span>

      {/* Content — lowercase 2-line headline, Play now + Claim pill */}
      <div
        className="relative z-[4] flex h-full flex-1 flex-col justify-end px-[16px] pb-[14px] pt-[34px] sm:max-w-[62%] sm:px-[22px]"
        style={{ maxWidth: "70%" }}
      >
        {/* One size below V11 — the longer headline needs the headroom to
            hold two lines inside the 136px shell at narrow widths. */}
        <h3
          className="m-0 font-black leading-[0.92] tracking-[-0.025em] text-white lowercase sm:text-[24px]"
          style={{ marginBottom: "auto", fontSize: "clamp(16px, 6vw, 24px)" }}
        >
          world cup picks
          <br />
          are live.
        </h3>
        <div className="mt-2 flex items-center">
          <span
            aria-hidden
            className="inline-flex h-6 items-center whitespace-nowrap rounded-full bg-white px-3 text-[11px] font-extrabold tracking-[-0.005em]"
            style={{
              color: "#C4135C",
              boxShadow:
                "0 4px 10px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.5)",
            }}
          >
            View picks
          </span>
        </div>
      </div>
    </Shell>
  );
}

/* V13 — First deposit match. Same type recipe as the Refer & Earn and 250x
   Pack banners it sits beside: mono ribbon, 24px bold headline, white number
   hero with a yellow full stop, white pill. The whole surface is the button
   and opens Add Funds. */
function V13({ onClaim, className }: Props) {
  const [addFundsOpen, setAddFundsOpen] = useState(false);

  return (
    <>
      <Shell
        className={className}
        onClaim={onClaim ?? (() => setAddFundsOpen(true))}
        ariaLabel="Deposit now — first deposit matched 100% up to $420"
        showDarkAccent={false}
      >
        {/* Yellow ribbon — same recipe as V11/V12 */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 z-[5] flex h-[22px] items-center gap-2.5 px-4 font-mono text-[8.5px] font-medium uppercase tracking-[0.20em]"
          style={{ background: YELLOW, color: "#1A1208" }}
        >
          <span>first deposit</span>
          <span aria-hidden className="block size-[3px] rounded-full" style={{ background: "#1A1208" }} />
          <span>new players</span>
        </div>

        {/* Number hero — the Refer & Earn figure's weight and tracking, with
            the dollar sign carrying the yellow. Capped one step lower because
            "$420" carries a fourth glyph in the same column width. The cap
            line hangs under it, matching the 250x Pack caption. */}
        <div
          className="pointer-events-none absolute z-[3] flex flex-col items-center"
          style={{ right: 14, top: "50%", transform: "translateY(calc(-50% + 11px))" }}
        >
          <span
            className="block whitespace-nowrap font-bold leading-none tracking-[-0.04em] text-white sm:text-[62px]"
            style={{ fontSize: "clamp(36px, 12vw, 62px)" }}
          >
            <span style={{ color: YELLOW }}>$</span>420
          </span>
          <span className="mt-1 whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.18em] text-white/70">
            100% match · up to
          </span>
        </div>

        {/* Copy — the Refer & Earn V3 container: pl-4, pt-[26px], gap-1.5,
            right padding reserving the figure's column. */}
        <div
          className="relative z-[4] flex h-full flex-col justify-center gap-1.5 pl-4 pt-[26px] sm:pr-[150px]"
          style={{ paddingRight: "clamp(104px, 38vw, 150px)" }}
        >
          <h3
            className="font-bold leading-[1.05] tracking-tight text-white sm:text-[24px]"
            style={{ fontSize: "clamp(15px, 5vw, 24px)" }}
          >
            100% Deposit Match.
            <br />
            Play with double.
          </h3>
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden
              className="inline-flex h-7 w-fit shrink-0 items-center whitespace-nowrap rounded-full bg-white px-4 text-[12px] font-semibold tracking-tight"
              style={{ color: "#C4135C" }}
            >
              Deposit now
            </span>
          </div>
        </div>
      </Shell>
      <AddFundsModal open={addFundsOpen} onOpenChange={setAddFundsOpen} />
    </>
  );
}

/* V14 — Number leads, flat fill. The figure is the subject: it sits first, a
   hairline rules it off from the sentence, and the CTA anchors the right
   edge. No ribbon, no watermark, one flat pink ground, the yellow carried by
   the figure alone.

   Type is the Refer & Earn banner's, to the pixel, because the two sit side
   by side: 24px bold headline on leading-[1.05], the h-7 white pill at 12px,
   and the headline's first line pinned to 39px from the top — where the
   neighbour's "Invite a friend." starts — so the two banners read on one
   line rather than each finding its own centre. */
function V14({ onClaim, className }: Props) {
  const [addFundsOpen, setAddFundsOpen] = useState(false);

  return (
    <>
      <Shell
        className={className}
        onClaim={onClaim ?? (() => setAddFundsOpen(true))}
        ariaLabel="Deposit now — your first deposit matched 100%, up to $420"
        showDarkAccent={false}
      >
        <div className="relative z-[4] flex h-full items-center gap-3.5 px-4 sm:gap-4">
          {/* The figure: the family's number-hero recipe, one size below the
              neighbour's $20. because it leads instead of closing. */}
          <span
            className="shrink-0 whitespace-nowrap font-bold leading-none tracking-[-0.04em] sm:text-[56px]"
            style={{ color: YELLOW, fontSize: "clamp(34px, 10vw, 56px)" }}
          >
            $420
          </span>

          {/* The rule: a hairline, not a plank. It only separates. */}
          <span aria-hidden className="h-[64px] w-px shrink-0 bg-white/25" />

          {/* Pinned, not centred: 39px is where the green banner's headline
              starts, so the first lines of both sit on the same row. */}
          <span className="min-w-0 flex-1 self-stretch pt-[39px]">
            <span
              className="block font-bold leading-[1.05] tracking-tight text-white sm:text-[24px]"
              style={{ fontSize: "clamp(15px, 5vw, 24px)" }}
            >
              Your first deposit,
              <br />
              matched 100%.
            </span>
          </span>

          <span
            aria-hidden
            className="inline-flex h-7 shrink-0 items-center whitespace-nowrap rounded-full bg-white px-4 text-[12px] font-semibold tracking-tight"
            style={{ color: "#C4135C" }}
          >
            Deposit now
          </span>
        </div>
      </Shell>
      <AddFundsModal open={addFundsOpen} onOpenChange={setAddFundsOpen} />
    </>
  );
}


/* Stylized football pitch — mowing stripes + halfway line, centre circle and a
   right-side penalty box, all white at low opacity. Reads as a stadium field
   behind the copy without competing with it. */
function Pitch() {
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      {/* Vertical mowing stripes */}
      <span
        className="absolute inset-0"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0 7%, rgba(255,255,255,0) 7% 14%)",
        }}
      />
      {/* Halfway line */}
      <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-white/15" />
      {/* Centre circle + spot */}
      <span className="absolute left-1/2 top-1/2 size-[120px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/15" />
      <span className="absolute left-1/2 top-1/2 size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/25" />
      {/* Right-side penalty box + arc */}
      <span className="absolute right-0 top-1/2 h-[104px] w-[58px] -translate-y-1/2 border-y border-l border-white/15" />
      <span className="absolute right-0 top-1/2 h-[52px] w-[24px] -translate-y-1/2 border-y border-l border-white/15" />
    </span>
  );
}

/* ─── Boost welcome-pack banner family ──────────────────────────────────────
   Single full-width banner shown above the Boost (bonus-eligible) grid. Blue is
   primary in every version; yellow is the lone accent, reserved for the $50
   value. Locked copy across all variants: Welcome pack / New players · Pick 3
   lit, get $50 free · Claim my $50 · 0 of 3 lit picks · Play $5 & unlock $50 ·
   5 × $10 boost picks. v1 is the original composition; v2-v5 are distinct
   directions, not micro-tweaks. */

const BOOST_BG = "linear-gradient(180deg, hsl(221 83% 55%) 0%, hsl(221 78% 46%) 100%)";

type BoostVariantProps = Props & { progress?: number; required?: number };

/* Shared copy primitives — identical text and styling in every variant. */
function BoostEyebrow() {
  return (
    <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/85">
      <span>Welcome pack</span>
      <span aria-hidden className="block h-px w-3 bg-white/45" />
      <span>New players</span>
    </div>
  );
}

function BoostClaimPill() {
  return (
    <span
      aria-hidden
      className="inline-flex h-7 items-center whitespace-nowrap rounded-full bg-white px-4 text-[12px] font-extrabold tracking-[-0.005em]"
      style={{
        color: BLUE_DARK,
        boxShadow: "0 4px 10px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.5)",
      }}
    >
      Claim my $50
    </span>
  );
}

function BoostProgress({ progress, required }: { progress: number; required: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-1" aria-hidden>
        {Array.from({ length: required }).map((_, i) => (
          <span key={i} className={cn("h-1 w-5 rounded-full", i < progress ? "bg-white" : "bg-white/30")} />
        ))}
      </div>
      <span className="whitespace-nowrap text-[11px] font-medium tracking-tight text-white/85">
        {progress} of {required} lit picks
      </span>
    </div>
  );
}

/* v1 — original: copy block left, $50 number-hero right, stadium pitch behind. */
function BoostV1({ onClaim, className, progress = 0, required = 3 }: BoostVariantProps) {
  return (
    <Shell onClaim={onClaim} ariaLabel="Claim $50 boost bonus" bg={BOOST_BG} showHalftone={false} showDarkAccent={false} className={className}>
      <Pitch />
      <div className="relative z-[4] flex h-full items-center justify-between gap-6 px-7 sm:px-10">
        <div className="flex max-w-[62%] flex-col justify-center gap-1.5">
          <BoostEyebrow />
          <h3 className="text-[22px] font-bold leading-[1.0] tracking-tight text-white sm:text-[24px]">
            Pick 3 lit,
            <br />
            get $50 free.
          </h3>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <BoostClaimPill />
            <BoostProgress progress={progress} required={required} />
          </div>
          <span className="text-[11px] font-medium tracking-tight text-white/65">Play $5 &amp; unlock $50</span>
        </div>
        <div className="shrink-0 text-right">
          <p className="font-bold leading-none tracking-[-0.04em]" style={{ fontSize: "clamp(48px, 12vw, 72px)", color: YELLOW }}>
            $50
          </p>
          <p className="mt-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-white/70">5 × $10 boost picks</p>
        </div>
      </div>
    </Shell>
  );
}

/* v2 — number monolith: a giant $50 anchors the left edge, copy stacks to its
   right across a thin divider. Value-first, magazine-cover weighting. */
function BoostV2({ onClaim, className, progress = 0, required = 3 }: BoostVariantProps) {
  return (
    <Shell onClaim={onClaim} ariaLabel="Claim $50 boost bonus" bg={BOOST_BG} showHalftone={false} showDarkAccent className={className}>
      <Pitch />
      <div className="relative z-[4] flex h-full items-center gap-5 px-7 sm:px-9">
        <div className="shrink-0 leading-none">
          <p className="font-bold tracking-[-0.05em]" style={{ fontSize: "clamp(56px, 13vw, 88px)", color: YELLOW }}>
            $50
          </p>
          <p className="mt-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-white/70">5 × $10 boost picks</p>
        </div>
        <span aria-hidden className="h-[86px] w-px bg-white/20" />
        <div className="flex min-w-0 flex-col justify-center gap-1.5">
          <BoostEyebrow />
          <h3 className="text-[20px] font-bold leading-[1.05] tracking-tight text-white sm:text-[22px]">Pick 3 lit, get $50 free.</h3>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <BoostClaimPill />
            <BoostProgress progress={progress} required={required} />
          </div>
          <span className="text-[11px] font-medium tracking-tight text-white/65">Play $5 &amp; unlock $50</span>
        </div>
      </div>
    </Shell>
  );
}

/* v3 — coupon stub: a perforated tear-off on the right holds the $50, echoing
   the "5 × $10 boost picks" as redeemable coupons. */
function BoostV3({ onClaim, className, progress = 0, required = 3 }: BoostVariantProps) {
  return (
    <Shell onClaim={onClaim} ariaLabel="Claim $50 boost bonus" bg={BOOST_BG} showHalftone={false} showDarkAccent={false} className={className}>
      <Pitch />
      <div className="relative z-[4] flex h-full items-stretch">
        <div className="flex flex-1 flex-col justify-center gap-1.5 px-7 sm:px-10">
          <BoostEyebrow />
          <h3 className="text-[22px] font-bold leading-[1.0] tracking-tight text-white sm:text-[24px]">
            Pick 3 lit,
            <br />
            get $50 free.
          </h3>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <BoostClaimPill />
            <BoostProgress progress={progress} required={required} />
          </div>
          <span className="text-[11px] font-medium tracking-tight text-white/65">Play $5 &amp; unlock $50</span>
        </div>
        <div className="relative flex w-[140px] shrink-0 flex-col items-center justify-center sm:w-[168px]" style={{ background: "rgba(255,255,255,0.06)" }}>
          <span
            aria-hidden
            className="absolute inset-y-3 left-0 w-px"
            style={{ backgroundImage: "repeating-linear-gradient(180deg, rgba(255,255,255,0.55) 0 5px, transparent 5px 10px)" }}
          />
          <span aria-hidden className="absolute -left-2 -top-2 size-4 rounded-full bg-background" />
          <span aria-hidden className="absolute -bottom-2 -left-2 size-4 rounded-full bg-background" />
          <p className="font-bold leading-none tracking-[-0.04em]" style={{ fontSize: "clamp(40px, 9vw, 56px)", color: YELLOW }}>
            $50
          </p>
          <p className="mt-1.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-white/75">5 × $10 boost picks</p>
        </div>
      </div>
    </Shell>
  );
}

/* v4 — bare editorial: flat blue, no pitch or texture, big type, $50 the only
   color. Maximum restraint — one accent, structural negative space. */
function BoostV4({ onClaim, className, progress = 0, required = 3 }: BoostVariantProps) {
  return (
    <Shell onClaim={onClaim} ariaLabel="Claim $50 boost bonus" bg="hsl(221 76% 45%)" showHalftone={false} showDarkAccent={false} className={className}>
      <span
        aria-hidden
        className="pointer-events-none absolute right-7 top-1/2 -translate-y-1/2 font-bold leading-none tracking-[-0.05em]"
        style={{ fontSize: 124, color: "rgba(255,255,255,0.07)" }}
      >
        50
      </span>
      <div className="relative z-[4] flex h-full flex-col justify-center gap-2 px-8 sm:px-12">
        <BoostEyebrow />
        <h3 className="text-[26px] font-bold leading-[0.98] tracking-tight text-white sm:text-[30px]">
          Pick 3 lit. Get <span style={{ color: YELLOW }}>$50</span> free.
        </h3>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <BoostClaimPill />
          <BoostProgress progress={progress} required={required} />
          <span className="text-[11px] font-medium tracking-tight text-white/60">Play $5 &amp; unlock $50</span>
        </div>
      </div>
    </Shell>
  );
}

const BOOST_VARIANTS = [BoostV1, BoostV2, BoostV3, BoostV4];
const BOOST_VARIANT_LABELS = [
  "v1 · original (copy left, $50 right)",
  "v2 · $50 monolith left",
  "v3 · coupon stub",
  "v4 · bare editorial",
] as const;
const BOOST_STORAGE_KEY = "alexey:boost-offer-banner";

const VARIANTS = [V1, V2, V3, V4, V5, V6, V7, V8, V9, V10, V11, V12, V13, V14];

/* ─── Public components ────────────────────────────────────────────────── */

/** Blue welcome-pack banner for Boost mode. Renders the active variant (v1-v5)
 *  with a small numbered switcher for design review; pass `forceVariant` to pin
 *  one and hide the switcher. */
export function BoostOfferBanner({ onClaim, className, forceVariant }: PublicProps) {
  const [variant, setVariant] = useExperimentVariant(BOOST_STORAGE_KEY, BOOST_VARIANTS.length);
  const active = forceVariant ?? variant;
  const Active = BOOST_VARIANTS[active - 1] ?? BoostV1;
  return (
    <div className={cn("relative", className)}>
      <Active onClaim={onClaim} className="w-full" />
      {forceVariant == null && (
        <div className="absolute right-2 top-2 z-20 flex gap-1">
          {BOOST_VARIANTS.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setVariant(i + 1);
              }}
              aria-label={`Show boost banner ${BOOST_VARIANT_LABELS[i]}`}
              className={cn(
                "size-5 rounded-full text-[10px] font-bold leading-none transition-colors",
                active === i + 1 ? "bg-white text-grey-900" : "bg-black/35 text-white/70 hover:text-white"
              )}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function OfferPromoBannerExperiment({ onClaim, className, forceVariant }: PublicProps) {
  const [hookVariant] = useExperimentVariant(STORAGE_KEY, VARIANTS.length);
  const variant = forceVariant ?? hookVariant;
  const Active = VARIANTS[variant - 1] ?? V1;
  return <Active onClaim={onClaim} className={className} />;
}

/* Header-mounted dropdown — selects the variant for the offer banner.
   Uses the same shared hook so updates flow to the banner immediately. */
export function OfferPromoBannerSwitcher({ className }: { className?: string }) {
  const [variant, setVariant] = useExperimentVariant(STORAGE_KEY, VARIANTS.length);
  return (
    <VariantPicker
      label="Offer"
      accent="#ec4899"
      variant={variant}
      labels={VARIANT_LABELS}
      onPick={setVariant}
      className={className}
    />
  );
}

interface PickerProps {
  label: string;
  accent: string;
  variant: number;
  labels: readonly string[];
  onPick: (n: number) => void;
  className?: string;
}

export function VariantPicker({ label, accent, variant, labels, onPick, className }: PickerProps) {
  const activeLabel = labels[variant - 1] ?? "";
  const [open, setOpen] = useState(false);
  return (
    <div className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-full border border-white/15 bg-grey-900 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-white/85 hover:bg-grey-800"
      >
        <span>{label}</span>
        <span className="text-white/55">·</span>
        <span>V{variant}</span>
        <span className="normal-case tracking-tight">· {activeLabel}</span>
        <span aria-hidden>{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <ul className="absolute left-0 top-full z-50 mt-1 w-[260px] overflow-hidden rounded-md border border-white/15 bg-black/95 py-1 shadow-[0_12px_30px_rgba(0,0,0,0.6)] backdrop-blur-md">
          {labels.map((l, i) => {
            const id = i + 1;
            return (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => {
                    onPick(id);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center justify-between px-3 py-1.5 text-left text-[11px] font-medium tracking-tight text-white/85 hover:bg-white/10",
                    id === variant && "bg-white/10",
                  )}
                >
                  <span>
                    <span className="font-mono text-white/40">V{id}</span> · {l}
                  </span>
                  {id === variant && <span style={{ color: accent }}>●</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
